import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { SMSFValidationEngine } from './SMSFValidationEngine.js';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const SMSFUnifiedDashboard = ({ smsfState, onNavigate }) => {
    const [dashboardData, setDashboardData] = useState(null);

    useEffect(() => {
        calculateDashboard();
    }, [smsfState]);

    const calculateDashboard = () => {
        // Extract data from smsfState
        const balance = smsfState.totalAssets || 0;
        const concessionalUsed = smsfState.smsfConcessionalUsed || 0;
        const nonConcessionalUsed = smsfState.smsfNonConcessionalUsed || 0;
        const tbcUsed = (smsfState.yourTbcUsed || 0) + (smsfState.partnerTbcUsed || 0);
        const inHouseAssets = (smsfState.smsfInHouseAssets || 0) + (smsfState.smsfRelatedPartyLoans || 0);
        const lrbaLoan = smsfState.smsfPropertyLoan || 0;
        const lrbaProperty = smsfState.smsfPropertyValue || 0;

        // Calculate key metrics
        const concessionalRemaining = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL - concessionalUsed;
        const nonConcessionalRemaining = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL - nonConcessionalUsed;
        const tbcRemaining = FinancialConfig.TRANSFER_BALANCE_CAP - tbcUsed;
        
        const inHousePercent = balance > 0 ? (inHouseAssets / balance) * 100 : 0;
        const lvrPercent = lrbaProperty > 0 ? (lrbaLoan / lrbaProperty) * 100 : 0;

        // Compliance status
        const complianceIssues = [];
        if (inHousePercent > 5) complianceIssues.push('In-house assets exceed 5%');
        if (lvrPercent > 80) complianceIssues.push('LVR exceeds 80%');
        if (concessionalUsed > FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL) complianceIssues.push('Concessional cap exceeded');
        if (nonConcessionalUsed > FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL) complianceIssues.push('Non-concessional cap exceeded');

        // Total fees
        const totalFees = (smsfState.smsfAuditFees || 0) + (smsfState.smsfAdminFees || 0) + (smsfState.smsfAccountingFees || 0);
        const feePercent = balance > 0 ? (totalFees / balance) * 100 : 0;

        // Quick actions
        const actions = [];
        if (concessionalRemaining > 5000) actions.push({ label: 'Use remaining $' + concessionalRemaining.toLocaleString() + ' concessional cap', nav: 'carry_forward', priority: 'high' });
        if (balance >= 500000 && lrbaProperty === 0) actions.push({ label: 'Consider LRBA for property', nav: 'lrba', priority: 'medium' });
        if (complianceIssues.length > 0) actions.push({ label: 'Review compliance issues', nav: 'compliance', priority: 'high' });

        setDashboardData({
            balance,
            contributions: { concessionalUsed, nonConcessionalUsed, concessionalRemaining, nonConcessionalRemaining },
            tbc: { used: tbcUsed, remaining: tbcRemaining },
            limits: { inHousePercent, lvrPercent, hasLRBA: lrbaProperty > 0 },
            fees: { total: totalFees, percent: feePercent },
            compliance: { issues: complianceIssues, healthy: complianceIssues.length === 0 },
            actions
        });
    };

    if (!dashboardData) return <div>Loading dashboard...</div>;

    return (
        <div className="max-w-7xl mx-auto p-6">
            <h2 className="text-3xl font-bold mb-6">📊 SMSF Dashboard</h2>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
                    <div className="text-sm opacity-90 mb-1">Fund Balance</div>
                    <div className="text-3xl font-bold">${(dashboardData.balance / 1000).toFixed(0)}K</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
                    <div className="text-sm opacity-90 mb-1">Compliance</div>
                    <div className="text-3xl font-bold">{dashboardData.compliance.healthy ? '✓' : '⚠️'} {dashboardData.compliance.healthy ? 'Healthy' : dashboardData.compliance.issues.length}</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
                    <div className="text-sm opacity-90 mb-1">Concessional Cap</div>
                    <div className="text-3xl font-bold">${(dashboardData.contributions.concessionalRemaining / 1000).toFixed(0)}K</div>
                    <div className="text-xs opacity-75">remaining</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg">
                    <div className="text-sm opacity-90 mb-1">Annual Fees</div>
                    <div className="text-3xl font-bold">{dashboardData.fees.percent.toFixed(2)}%</div>
                </div>
            </div>

            {/* Action Items */}
            {dashboardData.actions.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="font-semibold text-lg mb-4">🎯 Recommended Actions</h3>
                    <div className="space-y-3">
                        {dashboardData.actions.map((action, i) => (
                            <div key={i} className={`flex justify-between items-center p-4 rounded-lg border-l-4 ${
                                action.priority === 'high' ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'
                            }`}>
                                <span>{action.label}</span>
                                <button
                                    onClick={() => onNavigate && onNavigate(action.nav)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    Go →
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Asset Allocation Pie Chart & Contribution Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Asset Allocation Chart */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">Asset Allocation</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={[
                                    { name: 'Australian Shares', value: 35, color: '#3B82F6' },
                                    { name: 'International Shares', value: 30, color: '#8B5CF6' },
                                    { name: 'Property', value: 15, color: '#10B981' },
                                    { name: 'Bonds', value: 15, color: '#F59E0B' },
                                    { name: 'Cash', value: 5, color: '#6B7280' }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {[
                                    { name: 'Australian Shares', value: 35, color: '#3B82F6' },
                                    { name: 'International Shares', value: 30, color: '#8B5CF6' },
                                    { name: 'Property', value: 15, color: '#10B981' },
                                    { name: 'Bonds', value: 15, color: '#F59E0B' },
                                    { name: 'Cash', value: 5, color: '#6B7280' }
                                ].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* YTD Contribution Summary */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">YTD Contributions ({new Date().getFullYear()})</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                            <span className="text-gray-700">Concessional</span>
                            <div className="text-right">
                                <div className="font-bold text-blue-600">${dashboardData.contributions.concessionalUsed.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">Remaining: ${dashboardData.contributions.concessionalRemaining.toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                            <span className="text-gray-700">Non-Concessional</span>
                            <div className="text-right">
                                <div className="font-bold text-purple-600">${dashboardData.contributions.nonConcessionalUsed.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">Remaining: ${dashboardData.contributions.nonConcessionalRemaining.toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="border-t pt-3 flex justify-between">
                            <span className="font-semibold">Total Contributed YTD</span>
                            <span className="font-bold text-lg">${(dashboardData.contributions.concessionalUsed + dashboardData.contributions.nonConcessionalUsed).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity Timeline & Pending Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Pending Actions */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">⚠️ Pending Actions</h3>
                    <div className="space-y-3">
                        <div className="flex items-start p-3 bg-yellow-50 rounded border-l-4 border-yellow-500">
                            <span className="text-yellow-600 mr-2">!</span>
                            <div>
                                <div className="font-medium">Investment Strategy Review Due</div>
                                <div className="text-sm text-gray-600">Due: 30 June {new Date().getFullYear()}</div>
                            </div>
                        </div>
                        <div className="flex items-start p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                            <span className="text-blue-600 mr-2">ℹ️</span>
                            <div>
                                <div className="font-medium">Annual Return Lodgment</div>
                                <div className="text-sm text-gray-600">Due: 31 October {new Date().getFullYear()}</div>
                            </div>
                        </div>
                        {dashboardData.compliance.issues.map((issue, i) => (
                            <div key={i} className="flex items-start p-3 bg-red-50 rounded border-l-4 border-red-500">
                                <span className="text-red-600 mr-2">✕</span>
                                <div>
                                    <div className="font-medium">{issue}</div>
                                    <div className="text-sm text-gray-600">Action required</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Activity Timeline */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">📅 Recent Activity</h3>
                    <div className="space-y-3">
                        <div className="flex items-start">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                            <div className="flex-1">
                                <div className="font-medium text-sm">Configuration updated</div>
                                <div className="text-xs text-gray-500">Today, {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                            <div className="flex-1">
                                <div className="font-medium text-sm">Fund balance updated</div>
                                <div className="text-xs text-gray-500">Today</div>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></div>
                            <div className="flex-1">
                                <div className="font-medium text-sm">Contribution recorded</div>
                                <div className="text-xs text-gray-500">2 days ago</div>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></div>
                            <div className="flex-1">
                                <div className="font-medium text-sm">Compliance check completed</div>
                                <div className="text-xs text-gray-500">1 week ago</div>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3"></div>
                            <div className="flex-1">
                                <div className="font-medium text-sm">Investment strategy reviewed</div>
                                <div className="text-xs text-gray-500">2 weeks ago</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Metrics & Quick Access */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Performance Metrics */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">📈 Performance Metrics</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                            <span className="text-gray-700">1 Year Return</span>
                            <span className="font-bold text-green-600">+7.2%</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                            <span className="text-gray-700">3 Year Return (p.a.)</span>
                            <span className="font-bold text-blue-600">+6.8%</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                            <span className="text-gray-700">5 Year Return (p.a.)</span>
                            <span className="font-bold text-purple-600">+8.1%</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                            * Performance shown is indicative based on typical SMSF allocation
                        </div>
                    </div>
                </div>

                {/* Quick Access Buttons */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-lg">⚡ Quick Access</h3>
                        <button
                            onClick={() => calculateDashboard()}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                            🔄 Refresh Data
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => onNavigate && onNavigate('carry_forward')}
                            className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg text-left transition"
                        >
                            <div className="text-2xl mb-1">📅</div>
                            <div className="text-sm font-medium">Carry-Forward</div>
                        </button>
                        <button
                            onClick={() => onNavigate && onNavigate('compliance')}
                            className="p-3 bg-green-50 hover:bg-green-100 rounded-lg text-left transition"
                        >
                            <div className="text-2xl mb-1">✅</div>
                            <div className="text-sm font-medium">Compliance</div>
                        </button>
                        <button
                            onClick={() => onNavigate && onNavigate('pension_optimizer')}
                            className="p-3 bg-purple-50 hover:bg-purple-100 rounded-lg text-left transition"
                        >
                            <div className="text-2xl mb-1">💰</div>
                            <div className="text-sm font-medium">Pension</div>
                        </button>
                        <button
                            onClick={() => onNavigate && onNavigate('reports')}
                            className="p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg text-left transition"
                        >
                            <div className="text-2xl mb-1">📄</div>
                            <div className="text-sm font-medium">Reports</div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Detailed Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="font-semibold mb-4">Contribution Capacity</h4>
                    <div className="space-y-3">
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span>Concessional</span>
                                <span>{dashboardData.contributions.concessionalUsed}/{FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full" style={{width: `${(dashboardData.contributions.concessionalUsed / FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL) * 100}%`}}></div>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span>Non-Concessional</span>
                                <span>{dashboardData.contributions.nonConcessionalUsed}/{FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-purple-600 h-2 rounded-full" style={{width: `${(dashboardData.contributions.nonConcessionalUsed / FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL) * 100}%`}}></div>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span>Transfer Balance Cap</span>
                                <span>{(dashboardData.tbc.used / 1000).toFixed(0)}K/{(FinancialConfig.TRANSFER_BALANCE_CAP / 1000).toFixed(0)}K</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-green-600 h-2 rounded-full" style={{width: `${(dashboardData.tbc.used / FinancialConfig.TRANSFER_BALANCE_CAP) * 100}%`}}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="font-semibold mb-4">Investment Limits</h4>
                    <div className="space-y-3">
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span>In-House Assets</span>
                                <span className={dashboardData.limits.inHousePercent > 5 ? 'text-red-600 font-semibold' : ''}>
                                    {dashboardData.limits.inHousePercent.toFixed(2)}% / 5%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className={`h-2 rounded-full ${dashboardData.limits.inHousePercent > 5 ? 'bg-red-600' : 'bg-green-600'}`} 
                                    style={{width: `${Math.min(dashboardData.limits.inHousePercent / 5 * 100, 100)}%`}}></div>
                            </div>
                        </div>
                        {dashboardData.limits.hasLRBA && (
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span>LRBA LVR</span>
                                    <span className={dashboardData.limits.lvrPercent > 80 ? 'text-red-600 font-semibold' : ''}>
                                        {dashboardData.limits.lvrPercent.toFixed(1)}% / 80%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className={`h-2 rounded-full ${dashboardData.limits.lvrPercent > 80 ? 'bg-red-600' : 'bg-green-600'}`} 
                                        style={{width: `${Math.min(dashboardData.limits.lvrPercent / 80 * 100, 100)}%`}}></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFUnifiedDashboard.propTypes = {
    smsfState: PropTypes.object.isRequired,
    onNavigate: PropTypes.func
};

export default SMSFUnifiedDashboard;

